// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux

import invariant from 'invariant';

import { messageTypes } from 'constants/values';
import { logError } from 'api/errors';
import { getMessage } from 'util/locale';

// Action Types
export const typePrefix = 'vra/messages/';
export const actions = [
  'ADD_MESSAGE',
  'CLEAR_MESSAGES',
  'CLEAR_MESSAGES_OF_TYPE',
  'CLOSE_MESSAGE'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});

// Reducer

export default function reducer (
  state=[],
  action={}
) {
  switch(action.type) {
    case actions.ADD_MESSAGE:
    {
      const newMessages = state.slice();
      const findI = newMessages.findIndex( (message) => message.id === action.payload.message.id );
      if (findI !== -1) { // Replace existing message with same ID as new one.
        newMessages[findI] = action.payload.message;
      } else { // No existing message to replace--just add to end.
        newMessages.push( action.payload.message );
      }
      return newMessages;
    }

    case actions.CLOSE_MESSAGE:
      return state.filter( (message) => message.id !== action.payload.id );

    case actions.CLEAR_MESSAGES:
      return [];

    case actions.CLEAR_MESSAGES_OF_TYPE:
      return state.filter( (message) => message.type !== action.payload.type );

    default:
      return state;
  }
}

// Action Creators

/**
 * Add a new message for display to the user. Use addModalMessage() for modal type.
 * @param messageType   Should be one of constants.messageTypes types other than MODAL.
 * @param id            (optional) Unique ID that can be assigned to message for retrieving it later. Assigning an ID
 *                                 also enforces that only one message with the same ID will be displayed.
 * @param testId        (optional) Text to be added as part of the prefix for the test-id atribbutes in 
 *                                 the toast and its buttons
 * @param text          (optional) Text to display in message.
 * @param toast         (optional) If true, message will disappear automatically after a delay.
 * @param buttons       (optional) Array of buttons, each containing an object with .className (CSS class to apply to
                                   button), .text (text to appear on button), .onClick (function to call when clicked).
 * @param title         (optional) Title to show
 */
export const addMessage = ({messageType, id, showDismiss = true, testId, text='', toast=false, buttons, title}) => {
  invariant(messageTypes[messageType], 'The message type must be registered in constants.');
  if (messageType === messageTypes.MODAL) {
    logError({message:'Use addModalMessage for modal messages.'});
  }
  const useId = id || parseInt(Math.random()*1000000, 10);
  return {
    type: actions.ADD_MESSAGE,
    payload: {
      message: {
        id: useId,
        type: messageType,
        testId,
        text,
        title,
        toast,
        buttons,
        showDismiss
      }
    }
  };
};

export const addToast = ({ buttons, messageType, showDismiss, testId, text, title }) => {
  return addMessage({ buttons, messageType, showDismiss, testId, text, title, toast: true });
};

export const addErrorToast = (text) => {
  return addToast({ messageType: messageTypes.ERROR, text, title: getMessage('title-text__warning') });
};

export const addSuccessToast = (text) => {
  return addToast({ messageType: messageTypes.SUCCESS, text, title: getMessage('title-text__success') });
};

/**
 * Add a new modal message for display to the user.
 *
 * @param id            (optional) Unique ID that can be assigned to message for retrieving it later. Assigning an ID
 *                                 also enforces that only one message with the same ID will be displayed.
 * @param blocking      (optional) If true, the dialog will be styled to indicate a pending operation, e.g. loading
 *                                 animation.
 * @param className     (optional) Applied to the wrapper class.
 * @param bodyHtmlMessageKey (optional) I18n message ID for message to put in body of dialog.
 * @param bodyHtmlMessageParams (optional) Params to insert in I18n message ID.
 * @param buttons       (optional) Array of buttons, each containing an object with .text (text to appear on button),
 *                                 .onClick (function to call when clicked). Will be displayed in footer area of
 *                                 dialog.
 * @param footerText    (optional) Text to show in footer.
 * @param name          (optional) Also applied to the wrapper class, with some prefixing. TODO--Would like to get rid
 *                                 of this since redundant with className, but leave it at least until all the old
 *                                 dialog code in place and working.
 * @param title             (optional) Title to show.
 * @param provideCloseButton (optional) Add a default button to close the modal, eg if dispatching a modal from a place
                                  you can't easily pass JSX or dispatch methods
 */
export const addModalMessage = ({
  id,
  blocking=false,
  bodyHtmlMessageKey,
  bodyHtmlMessageParams,
  className,
  footerText,
  buttons,
  title,
  name,
  provideCloseButton=false
}) => {
  const useId = id || parseInt(Math.random()*1000000, 10);
  return {
    type: actions.ADD_MESSAGE,
    payload: {
      message: {
        id: useId,
        type: messageTypes.MODAL,
        title,
        blocking,
        bodyHtmlMessageKey,
        bodyHtmlMessageParams,
        className,
        footerText,
        buttons,
        name,
        provideCloseButton
      }
    }
  };
};

/**
 * Close a message, removing it from store.
 * @param id            ID of message to close.
 */
export const closeMessage = (id) => {
  return {
    type: actions.CLOSE_MESSAGE,
    payload: {
      id: id
    }
  };
};

/**
 * Clear all messages, removing from store.
 */
export const clearMessages = () => {
  return {
    type: actions.CLEAR_MESSAGES
  };
};

/**
 * Clear all messages of a type, removing from store.
 * @param messageType            One of the messageTypes constants.
 */
export const clearMessagesOfType = (messageType) => {
  return {
    type: actions.CLEAR_MESSAGES_OF_TYPE,
    payload: {
      type: messageType
    }
  };
};

export const addTransitionModal = ({ id, bodyHtmlMessageKey, bodyHtmlMessageParams, className, title }) => {
  return addModalMessage({ id, blocking: true, bodyHtmlMessageKey, bodyHtmlMessageParams, className, title});
};

export const removeTransitionModal = ({ id }) => {
  return closeMessage(id);
};
